/**
 * Backend error codes as defined in the API swagger specification.
 * These numeric codes are returned in the `code` field of API error responses.
 *
 * To add new codes: update this enum, BACKEND_ERROR_CODE_TRANSLATIONS below,
 * and `common:backendErrors` in `public/locales/en/common.json`.
 *
 * AUTO-GENERATED — run `pnpm sync:error-codes` to update from swagger.
 */
export enum BackendErrorCode {
  // General (1xxx)
  INTERNAL_SERVER_ERROR = 1000,
  BAD_REQUEST = 1001,
  NOT_FOUND = 1002,
  UNAUTHORIZED = 1003,
  FORBIDDEN = 1004,
  CONFLICT = 1005,
  ACCESS_ERROR_NO_ACTIVE_SUBSCRIPTIONS_FOUND = 1100,
  ACCESS_ERROR_ACCESS_REVOKED = 1101,

  // Assignments (2000–2099)
  ASSIGNMENT_SYNC_TOKEN_MISMATCH = 2000,
  ASSIGNMENT_NOT_FOUND = 2001,
  ASSIGNMENT_INVALID_CUT_DATE = 2002,
  ASSIGNMENT_CUT_DATE_INVALID = 2003,
  ASSIGNMENT_OVERLAP_DETECTED = 2004,
  ASSIGNMENT_INVALID_DATE_RANGE = 2006,
  ASSIGNMENT_INACTIVE_MEMBER = 2007,
  ASSIGNMENT_INACTIVE_PROJECT_WITH_PROJECT_BASED_RATE = 2008,
  ASSIGNMENT_FULLY_OVERLAPPED_BY_TIMEOFF = 2009,
  PLANNER_FILTERS_INVALID_DATE_RANGE = 2010,
  PLANNER_AVAILABILITY_FILTER_REQUIRES_FILTERS_DATE_RANGE = 2011,
  PLANNER_AVAILABILITY_FILTER_INVALID_BOUNDS = 2012,
  PLANNER_FILTERS_DATE_RANGE_TOO_LONG = 2013,

  // Users (2100–2199)
  USER_NOT_FOUND = 2100,
  CANNOT_DELETE_CHAMPION_USER = 2101,
  USER_CANNOT_MODIFY_ARCHIVED_STATE = 2102,
  USER_DUPLICATE_NAME_EMAIL_PAIR = 2103,
  USER_CANNOT_CHANGE_EMAIL_WHEN_ACTIVE_OR_INVITED = 2104,
  TEAM_MEMBER_DUPLICATE_NAME = 2105,
  INVITE_TEAM_MEMBER_NOT_FOUND = 2106,
  INVITE_GROUP_NOT_FOUND = 2107,
  INVITE_PLACEHOLDER_CANNOT_BE_INVITED = 2108,
  INVITE_ALREADY_INVITED = 2109,
  INVITE_NO_EMAIL = 2110,
  INVITE_NO_DEFAULT_GROUP = 2111,
  USER_NEEDS_REVIEW = 2112,

  // Projects (2200–2299)
  PROJECT_NOT_FOUND = 2200,
  PROJECT_UNARCHIVATION_DATE_ALREADY_SET = 2201,
  PROJECT_CANNOT_MODIFY_ARCHIVED_STATE = 2202,
  PROJECT_ARCHIVATION_DATE_OUT_OF_THE_AVAILABLE_RANGE = 2203,
  PROJECT_DUPLICATE_NAME = 2204,

  // Invoices (2300–2399)
  INVOICE_NOT_FOUND = 2300,
  INVOICE_STILL_CREATING = 2301,
  INVOICE_NEGATIVE_TOTAL = 2302,
  INVOICE_SYNC_TOKEN_MISMATCH = 2303,
  INVOICE_NOT_EDITABLE = 2304,

  // Bills (2400–2499)
  BILL_NOT_FOUND = 2400,
  BILL_STILL_CREATING = 2401,
  BILL_NEGATIVE_TOTAL = 2402,
  BILL_SYNC_TOKEN_MISMATCH = 2403,
  BILL_NOT_EDITABLE = 2404,

  // QuickBooks (2500–2599)
  QB_COMPANY_ID_IS_ALREADY_CONNECTED = 2500,
  QB_CUSTOMER_DUPLICATE_NAME = 2501,
  QB_PAYMENT_TERM_NOT_AVAILABLE = 2502,
  QB_CUSTOMER_UPDATE_REJECTED = 2503,
  QB_CUSTOMER_UPDATE_FAILED = 2504,
  QB_CUSTOMER_INACTIVE = 2505,
  QB_CUSTOMER_CREATE_FAILED = 2506,
  QB_CLIENT_SYNC_IN_PROGRESS = 2507,

  // Worklogs (2600–2699)
  WORKLOG_APPROVAL_CONFLICT = 2600,
  WORKLOG_IS_APPROVED = 2601,
  ACTIVE_TIMER_EXISTS = 2602,
  ACTIVE_TIMER_NOT_FOUND = 2603,
  ACTIVE_TIMER_LIMIT_REACHED = 2604,
  WORKLOG_DAILY_LIMIT_EXCEEDED = 2605,
  WORKLOG_EXCEEDS_MIDNIGHT = 2606,
  WORKLOG_APPROVED_SYNC_CONFLICT = 2607,

  // Other (2700–2799)
  FILE_SIZE_EXCEEDED = 2700,
  INVALID_FILE_EXTENSION = 2701,

  // Other (2800–2899)
  TIME_OFF_REQUEST_NOT_FOUND = 2800,
  TIME_OFF_REQUEST_INVALID_STATUS = 2801,
  TIME_OFF_REQUEST_NOT_OWNED = 2802,
  TIME_OFF_TYPE_NOT_FOUND = 2803,
  TIME_OFF_REQUEST_IS_NOT_PENDING = 2804,
  TIME_OFF_REQUEST_APPROVAL_IS_NOT_PENDING = 2805,
  TIME_OFF_POLICY_NOT_FOUND = 2806,
  TIME_OFF_POLICY_CANNOT_DELETE_ACTIVE_OR_PAST = 2807,
  TIME_OFF_REQUEST_DAY_VALIDATION_ERRORS = 2808,

  // Other (2900–2999)
  CLIENT_DUPLICATE_NAME = 2900,

  // Other (3000–3099)
  CUSTOM_ATTRIBUTE_NOT_FOUND = 3000,
  CUSTOM_ATTRIBUTE_DUPLICATE_NAME = 3001,

  // Other (3100–3199)
  PAYMENT_DETAILS_NOT_FOUND = 3100,
  PAYMENT_DETAILS_NAME_DUPLICATE = 3101,

  // Other (3200–3299)
  OVERHEAD_NOT_FOUND = 3200,
  OVERHEAD_ALREADY_INVOICED = 3201,
  OVERHEAD_INVALID_STATUS_TRANSITION = 3202,
  OVERHEAD_INVALID_AMOUNT = 3203,

  // Other (3300–3399)
  REPORT_NOT_FOUND = 3300,
  REPORT_DATASET_NOT_FOUND = 3301,
  FORMULA_ENTRY_NOT_FOUND = 3302,
  REPORT_ELEMENT_NOT_FOUND = 3303,
  REPORT_ELEMENT_POSITION_CONFLICT = 3304,
  REPORT_IS_SYSTEM = 3305,
  REPORT_ELEMENT_NO_DATA_CONFIG = 3306,
  REPORT_FORMULA_INVALID = 3307,
  REPORT_INVALID_QUERY_COLUMN = 3308,

  // Other (3400–3499)
  ENTITY_SYNC_INTERNAL_ERROR = 3400,
  ENTITY_SYNC_INTEGRATION_VALIDATION_ERROR = 3401,
  ENTITY_SYNC_INTEGRATION_AUTHENTICATION_ERROR = 3402,
  ENTITY_SYNC_INTEGRATION_PERMISSION_ERROR = 3403,
  ENTITY_SYNC_INTEGRATION_SERVICE_UNAVAILABLE = 3404,
  ENTITY_SYNC_INTEGRATION_RATE_LIMIT_ERROR = 3405,
  ENTITY_SYNC_INTEGRATION_GENERAL_ERROR = 3406,

  // Other (3500–3599)
  USER_GROUP_SUPER_ADMIN_CANNOT_BE_DEFAULT = 3500,
}

/**
 * Maps backend numeric error codes to i18n translation keys.
 * Translation keys resolve under `common:backendErrors.*`.
 */
export const BACKEND_ERROR_CODE_TRANSLATIONS: Record<number, string> = {
  [BackendErrorCode.INTERNAL_SERVER_ERROR]: 'common:backendErrors.1000',
  [BackendErrorCode.BAD_REQUEST]: 'common:backendErrors.1001',
  [BackendErrorCode.NOT_FOUND]: 'common:backendErrors.1002',
  [BackendErrorCode.UNAUTHORIZED]: 'common:backendErrors.1003',
  [BackendErrorCode.FORBIDDEN]: 'common:backendErrors.1004',
  [BackendErrorCode.CONFLICT]: 'common:backendErrors.1005',
  [BackendErrorCode.ACCESS_ERROR_NO_ACTIVE_SUBSCRIPTIONS_FOUND]: 'common:backendErrors.1100',
  [BackendErrorCode.ACCESS_ERROR_ACCESS_REVOKED]: 'common:backendErrors.1101',
  [BackendErrorCode.ASSIGNMENT_SYNC_TOKEN_MISMATCH]: 'common:backendErrors.2000',
  [BackendErrorCode.ASSIGNMENT_NOT_FOUND]: 'common:backendErrors.2001',
  [BackendErrorCode.ASSIGNMENT_INVALID_CUT_DATE]: 'common:backendErrors.2002',
  [BackendErrorCode.ASSIGNMENT_CUT_DATE_INVALID]: 'common:backendErrors.2003',
  [BackendErrorCode.ASSIGNMENT_OVERLAP_DETECTED]: 'common:backendErrors.2004',
  [BackendErrorCode.ASSIGNMENT_INVALID_DATE_RANGE]: 'common:backendErrors.2006',
  [BackendErrorCode.ASSIGNMENT_INACTIVE_MEMBER]: 'common:backendErrors.2007',
  [BackendErrorCode.ASSIGNMENT_INACTIVE_PROJECT_WITH_PROJECT_BASED_RATE]:
    'common:backendErrors.2008',
  [BackendErrorCode.ASSIGNMENT_FULLY_OVERLAPPED_BY_TIMEOFF]: 'common:backendErrors.2009',
  [BackendErrorCode.PLANNER_FILTERS_INVALID_DATE_RANGE]: 'common:backendErrors.2010',
  [BackendErrorCode.PLANNER_AVAILABILITY_FILTER_REQUIRES_FILTERS_DATE_RANGE]:
    'common:backendErrors.2011',
  [BackendErrorCode.PLANNER_AVAILABILITY_FILTER_INVALID_BOUNDS]: 'common:backendErrors.2012',
  [BackendErrorCode.PLANNER_FILTERS_DATE_RANGE_TOO_LONG]: 'common:backendErrors.2013',
  [BackendErrorCode.USER_NOT_FOUND]: 'common:backendErrors.2100',
  [BackendErrorCode.CANNOT_DELETE_CHAMPION_USER]: 'common:backendErrors.2101',
  [BackendErrorCode.USER_CANNOT_MODIFY_ARCHIVED_STATE]: 'common:backendErrors.2102',
  [BackendErrorCode.USER_DUPLICATE_NAME_EMAIL_PAIR]: 'common:backendErrors.2103',
  [BackendErrorCode.USER_CANNOT_CHANGE_EMAIL_WHEN_ACTIVE_OR_INVITED]: 'common:backendErrors.2104',
  [BackendErrorCode.TEAM_MEMBER_DUPLICATE_NAME]: 'common:backendErrors.2105',
  [BackendErrorCode.INVITE_TEAM_MEMBER_NOT_FOUND]: 'common:backendErrors.2106',
  [BackendErrorCode.INVITE_GROUP_NOT_FOUND]: 'common:backendErrors.2107',
  [BackendErrorCode.INVITE_PLACEHOLDER_CANNOT_BE_INVITED]: 'common:backendErrors.2108',
  [BackendErrorCode.INVITE_ALREADY_INVITED]: 'common:backendErrors.2109',
  [BackendErrorCode.INVITE_NO_EMAIL]: 'common:backendErrors.2110',
  [BackendErrorCode.INVITE_NO_DEFAULT_GROUP]: 'common:backendErrors.2111',
  [BackendErrorCode.USER_NEEDS_REVIEW]: 'common:backendErrors.2112',
  [BackendErrorCode.PROJECT_NOT_FOUND]: 'common:backendErrors.2200',
  [BackendErrorCode.PROJECT_UNARCHIVATION_DATE_ALREADY_SET]: 'common:backendErrors.2201',
  [BackendErrorCode.PROJECT_CANNOT_MODIFY_ARCHIVED_STATE]: 'common:backendErrors.2202',
  [BackendErrorCode.PROJECT_ARCHIVATION_DATE_OUT_OF_THE_AVAILABLE_RANGE]:
    'common:backendErrors.2203',
  [BackendErrorCode.PROJECT_DUPLICATE_NAME]: 'common:backendErrors.2204',
  [BackendErrorCode.INVOICE_NOT_FOUND]: 'common:backendErrors.2300',
  [BackendErrorCode.INVOICE_STILL_CREATING]: 'common:backendErrors.2301',
  [BackendErrorCode.INVOICE_NEGATIVE_TOTAL]: 'common:backendErrors.2302',
  [BackendErrorCode.INVOICE_SYNC_TOKEN_MISMATCH]: 'common:backendErrors.2303',
  [BackendErrorCode.INVOICE_NOT_EDITABLE]: 'common:backendErrors.2304',
  [BackendErrorCode.BILL_NOT_FOUND]: 'common:backendErrors.2400',
  [BackendErrorCode.BILL_STILL_CREATING]: 'common:backendErrors.2401',
  [BackendErrorCode.BILL_NEGATIVE_TOTAL]: 'common:backendErrors.2402',
  [BackendErrorCode.BILL_SYNC_TOKEN_MISMATCH]: 'common:backendErrors.2403',
  [BackendErrorCode.BILL_NOT_EDITABLE]: 'common:backendErrors.2404',
  [BackendErrorCode.QB_COMPANY_ID_IS_ALREADY_CONNECTED]: 'common:backendErrors.2500',
  [BackendErrorCode.QB_CUSTOMER_DUPLICATE_NAME]: 'common:backendErrors.2501',
  [BackendErrorCode.QB_PAYMENT_TERM_NOT_AVAILABLE]: 'common:backendErrors.2502',
  [BackendErrorCode.QB_CUSTOMER_UPDATE_REJECTED]: 'common:backendErrors.2503',
  [BackendErrorCode.QB_CUSTOMER_UPDATE_FAILED]: 'common:backendErrors.2504',
  [BackendErrorCode.QB_CUSTOMER_INACTIVE]: 'common:backendErrors.2505',
  [BackendErrorCode.QB_CUSTOMER_CREATE_FAILED]: 'common:backendErrors.2506',
  [BackendErrorCode.QB_CLIENT_SYNC_IN_PROGRESS]: 'common:backendErrors.2507',
  [BackendErrorCode.WORKLOG_APPROVAL_CONFLICT]: 'common:backendErrors.2600',
  [BackendErrorCode.WORKLOG_IS_APPROVED]: 'common:backendErrors.2601',
  [BackendErrorCode.ACTIVE_TIMER_EXISTS]: 'common:backendErrors.2602',
  [BackendErrorCode.ACTIVE_TIMER_NOT_FOUND]: 'common:backendErrors.2603',
  [BackendErrorCode.ACTIVE_TIMER_LIMIT_REACHED]: 'common:backendErrors.2604',
  [BackendErrorCode.WORKLOG_DAILY_LIMIT_EXCEEDED]: 'common:backendErrors.2605',
  [BackendErrorCode.WORKLOG_EXCEEDS_MIDNIGHT]: 'common:backendErrors.2606',
  [BackendErrorCode.WORKLOG_APPROVED_SYNC_CONFLICT]: 'common:backendErrors.2607',
  [BackendErrorCode.FILE_SIZE_EXCEEDED]: 'common:backendErrors.2700',
  [BackendErrorCode.INVALID_FILE_EXTENSION]: 'common:backendErrors.2701',
  [BackendErrorCode.TIME_OFF_REQUEST_NOT_FOUND]: 'common:backendErrors.2800',
  [BackendErrorCode.TIME_OFF_REQUEST_INVALID_STATUS]: 'common:backendErrors.2801',
  [BackendErrorCode.TIME_OFF_REQUEST_NOT_OWNED]: 'common:backendErrors.2802',
  [BackendErrorCode.TIME_OFF_TYPE_NOT_FOUND]: 'common:backendErrors.2803',
  [BackendErrorCode.TIME_OFF_REQUEST_IS_NOT_PENDING]: 'common:backendErrors.2804',
  [BackendErrorCode.TIME_OFF_REQUEST_APPROVAL_IS_NOT_PENDING]: 'common:backendErrors.2805',
  [BackendErrorCode.TIME_OFF_POLICY_NOT_FOUND]: 'common:backendErrors.2806',
  [BackendErrorCode.TIME_OFF_POLICY_CANNOT_DELETE_ACTIVE_OR_PAST]: 'common:backendErrors.2807',
  [BackendErrorCode.TIME_OFF_REQUEST_DAY_VALIDATION_ERRORS]: 'common:backendErrors.2808',
  [BackendErrorCode.CLIENT_DUPLICATE_NAME]: 'common:backendErrors.2900',
  [BackendErrorCode.CUSTOM_ATTRIBUTE_NOT_FOUND]: 'common:backendErrors.3000',
  [BackendErrorCode.CUSTOM_ATTRIBUTE_DUPLICATE_NAME]: 'common:backendErrors.3001',
  [BackendErrorCode.PAYMENT_DETAILS_NOT_FOUND]: 'common:backendErrors.3100',
  [BackendErrorCode.PAYMENT_DETAILS_NAME_DUPLICATE]: 'common:backendErrors.3101',
  [BackendErrorCode.OVERHEAD_NOT_FOUND]: 'common:backendErrors.3200',
  [BackendErrorCode.OVERHEAD_ALREADY_INVOICED]: 'common:backendErrors.3201',
  [BackendErrorCode.OVERHEAD_INVALID_STATUS_TRANSITION]: 'common:backendErrors.3202',
  [BackendErrorCode.OVERHEAD_INVALID_AMOUNT]: 'common:backendErrors.3203',
  [BackendErrorCode.REPORT_NOT_FOUND]: 'common:backendErrors.3300',
  [BackendErrorCode.REPORT_DATASET_NOT_FOUND]: 'common:backendErrors.3301',
  [BackendErrorCode.FORMULA_ENTRY_NOT_FOUND]: 'common:backendErrors.3302',
  [BackendErrorCode.REPORT_ELEMENT_NOT_FOUND]: 'common:backendErrors.3303',
  [BackendErrorCode.REPORT_ELEMENT_POSITION_CONFLICT]: 'common:backendErrors.3304',
  [BackendErrorCode.REPORT_IS_SYSTEM]: 'common:backendErrors.3305',
  [BackendErrorCode.REPORT_ELEMENT_NO_DATA_CONFIG]: 'common:backendErrors.3306',
  [BackendErrorCode.REPORT_FORMULA_INVALID]: 'common:backendErrors.3307',
  [BackendErrorCode.REPORT_INVALID_QUERY_COLUMN]: 'common:backendErrors.3308',
  [BackendErrorCode.ENTITY_SYNC_INTERNAL_ERROR]: 'common:backendErrors.3400',
  [BackendErrorCode.ENTITY_SYNC_INTEGRATION_VALIDATION_ERROR]: 'common:backendErrors.3401',
  [BackendErrorCode.ENTITY_SYNC_INTEGRATION_AUTHENTICATION_ERROR]: 'common:backendErrors.3402',
  [BackendErrorCode.ENTITY_SYNC_INTEGRATION_PERMISSION_ERROR]: 'common:backendErrors.3403',
  [BackendErrorCode.ENTITY_SYNC_INTEGRATION_SERVICE_UNAVAILABLE]: 'common:backendErrors.3404',
  [BackendErrorCode.ENTITY_SYNC_INTEGRATION_RATE_LIMIT_ERROR]: 'common:backendErrors.3405',
  [BackendErrorCode.ENTITY_SYNC_INTEGRATION_GENERAL_ERROR]: 'common:backendErrors.3406',
  [BackendErrorCode.USER_GROUP_SUPER_ADMIN_CANNOT_BE_DEFAULT]: 'common:backendErrors.3500',
};
